import { createContext, useContext, useState, ReactNode } from "react";

interface Artwork {
  id: string;
  title: string;
  artist?: string;
  image_url: string;
  description?: string;
  estimated_value?: number;
  views?: number;
  size?: string;
  weight?: string;
  location_city?: string;
  location_country?: string;
  creation_date?: string;
  owner?: string;
  purchase_source?: string;
  materials?: string;
  artist_bio?: string;
  creation_reason?: string;
}

interface ComparisonContextType {
  comparisonList: Artwork[];
  addToComparison: (artwork: Artwork) => boolean;
  removeFromComparison: (id: string) => void;
  clearComparison: () => void;
  isInComparison: (id: string) => boolean;
  maxItems: number;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: ReactNode }) => {
  const [comparisonList, setComparisonList] = useState<Artwork[]>([]);
  const maxItems = 4;

  const addToComparison = (artwork: Artwork): boolean => {
    if (comparisonList.length >= maxItems) {
      return false;
    }
    if (comparisonList.some(a => a.id === artwork.id)) {
      return true;
    }
    setComparisonList(prev => [...prev, artwork]);
    return true;
  };

  const removeFromComparison = (id: string) => {
    setComparisonList(prev => prev.filter(a => a.id !== id));
  };

  const clearComparison = () => {
    setComparisonList([]);
  };

  const isInComparison = (id: string): boolean => {
    return comparisonList.some(a => a.id === id);
  };

  return (
    <ComparisonContext.Provider
      value={{
        comparisonList,
        addToComparison,
        removeFromComparison,
        clearComparison,
        isInComparison,
        maxItems,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error("useComparison must be used within a ComparisonProvider");
  }
  return context;
};
